import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { GalleryPhoto } from '../data/gallery';
interface GalleryLightboxProps {
  photos: GalleryPhoto[];
  index: number | null;
  onChange: (index: number | null) => void;
}
export function GalleryLightbox({
  photos,
  index,
  onChange
}: GalleryLightboxProps) {
  const {
    t
  } = useLanguage();
  const open = index !== null;
  const photo = open ? photos[index] : null;
  const step = (dir: number) => {
    if (index === null) return;
    onChange((index + dir + photos.length) % photos.length);
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChange(null);
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  });
  const navButton = 'absolute top-1/2 -translate-y-1/2 rounded-full bg-cream/10 p-3 text-cream transition-colors duration-150 hover:bg-cream/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-hay';
  return <AnimatePresence data-id="element-134" data-mp="21quvk50m7pdm">
      {photo && <motion.div role="dialog" aria-modal="true" aria-label={t.gallery.title} className="fixed inset-0 z-50 flex items-center justify-center bg-bark/95 p-4 sm:p-10" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onClick={() => onChange(null)} data-id="element-135" data-mp="1wrroyg4zlb3j">
          <figure className="flex max-h-full flex-col items-center" onClick={e => e.stopPropagation()} data-id="element-136" data-mp="3i5dylqkgbyuu">
            <motion.img key={photo.id} src={photo.src} alt={`${t.gallery.title} ${(index ?? 0) + 1}`} initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-h-[82vh] w-auto max-w-full rounded-md object-contain" data-id="element-137" data-mp="2eugp9wy7871q" />
            <figcaption className="mt-4 text-sm text-cream/70" data-id="element-138" data-mp="1w4ig1dio07wi">
              {(index ?? 0) + 1} / {photos.length}
            </figcaption>
          </figure>

          <button type="button" onClick={e => {
        e.stopPropagation();
        step(-1);
      }} aria-label={t.gallery.prev} className={`${navButton} left-3 sm:left-6`} data-id="element-139" data-mp="2545hfzbe9dpk">
            <ChevronLeftIcon className="h-6 w-6" data-id="element-140" data-mp="29wf3h49e1tmk" />
          </button>
          <button type="button" onClick={e => {
        e.stopPropagation();
        step(1);
      }} aria-label={t.gallery.next} className={`${navButton} right-3 sm:right-6`} data-id="element-141" data-mp="1ddr9sktz4xmc">
            <ChevronRightIcon className="h-6 w-6" data-id="element-142" data-mp="1hc6swdmxpluh" />
          </button>
          <button type="button" autoFocus onClick={() => onChange(null)} aria-label={t.gallery.close} className="absolute right-4 top-4 rounded-full p-2 text-cream/80 transition-colors duration-150 hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-hay" data-id="element-143" data-mp="19uiegb4nn83t">
            <XIcon className="h-6 w-6" data-id="element-144" data-mp="qvwdqxd1vj0f" />
          </button>
        </motion.div>}
    </AnimatePresence>;
}